/**
 * Curated stock imagery per vendor category, used whenever a vendor (or category)
 * has no uploaded photo of its own. Replaces random picsum.photos placeholders,
 * which served unrelated stock shots — forests and office towers on wedding cards.
 *
 * Every photo here is a Nepali or South Asian wedding scene: traditional attire,
 * mandap and marigold decor, mehendi, dhol and baja, thali feasts, bridal gold.
 * All IDs were verified to return HTTP 200 before being added — if you add more,
 * check the URL first, since a wrong ID silently renders a broken card.
 *
 * Stored without size params so each call site can request its own dimensions.
 */

import mediaUrl from './mediaUrl';

const pexels = (id) => `https://images.pexels.com/photos/${id}/pexels-photo-${id}.jpeg`;

/** Nepali couples in traditional dress — safe stand-in for any category. */
const GENERIC = [pexels(38259819), pexels(31820500), pexels(12543680), pexels(29137533)];

/**
 * First entry of each list is the primary — it is also what the homepage category
 * tiles use, so a category reads consistently across the site.
 */
const CATEGORY_IMAGES = {
  // Mandap and banquet settings
  'venues': [pexels(34079355), pexels(33417236), pexels(33417234)],
  // South Asian couples being photographed
  'photographers': [pexels(31798269), pexels(20610661), pexels(35325793)],
  // Thali spreads and wedding feasts
  'caterers': [pexels(8818732), pexels(29850004), pexels(8148149)],
  // Marigold arches, garland-draped stages
  'decorators': [pexels(33508474), pexels(14395559), pexels(12432503)],
  // Bridal mehendi close-ups
  'mehendi-artists': [pexels(38230058), pexels(39042171), pexels(34245192)],
  // Baraat drummers and wedding bands
  'djs-music': [pexels(8155771), pexels(4552397), pexels(32652559)],
  // Traditional drums and processional players
  'panche-baja': [pexels(37093183), pexels(11094777), pexels(8155771)],
  // Bride in the makeup chair
  'beauty-makeup': [pexels(29516468), pexels(29516470), pexels(11742214)],
  // Marigold garlands and flower work
  'florists': [pexels(11255470), pexels(8270261), pexels(12718218)],
  // Vedic ceremony and ritual hands
  'pandits-priests': [pexels(4128977), pexels(12060074), pexels(36814776)],
  // Nepali couples, full ceremony scenes
  'wedding-planners': [pexels(38259819), pexels(31820500), pexels(11652319)],
  // Traditional card on red fabric
  'invitation-cards': [pexels(30921929), pexels(14961296), pexels(35629390)],
  // Gold sets, bangles, bridal ornaments
  'jewellers': [pexels(29038003), pexels(35059564), pexels(29037987)],
  // Sarees, lehengas, bridal wear
  'bridal-wear': [pexels(38773208), pexels(13048652), pexels(16141929)],
};

function buildUrl(base, width, height) {
  return `${base}?auto=compress&cs=tinysrgb&w=${width}&h=${height}&fit=crop`;
}

/**
 * Google Places photo URLs are billed per request and, once the project's quota
 * or billing lapses, Google answers 403 with an error PNG — which a browser
 * renders happily as a red crossed-out map rather than firing onError. So these
 * cannot be repaired by a load-failure fallback; they have to be skipped before
 * they ever reach an <img>. Skipping them also stops the paid calls entirely.
 */
export function isPaidGooglePhoto(url) {
  return typeof url === 'string' && url.includes('maps.googleapis.com/maps/api/place/photo');
}

/** Stable per-vendor index so a card keeps the same photo across re-renders. */
function hash(str) {
  const s = String(str);
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/** Primary image for a category — used for category tiles and as the error fallback. */
export function categoryImage(slug, width = 800, height = 500) {
  const list = CATEGORY_IMAGES[slug] || GENERIC;
  return buildUrl(list[0], width, height);
}

/**
 * Image for a vendor: their own upload if present, otherwise a category-appropriate
 * photo varied by vendor so a filtered listing doesn't repeat one image down the page.
 */
export function vendorImage(vendor, width = 400, height = 260) {
  const cover = vendor?.cover_photo_url;
  // Uploaded covers are stored as /uploads/... paths; resolve them against the
  // API origin or the Android app (served from https://localhost) can't load them.
  if (cover && !isPaidGooglePhoto(cover)) return mediaUrl(cover);
  const list = CATEGORY_IMAGES[vendor?.category_slug] || GENERIC;
  const key = vendor?.slug || vendor?.id || '';
  return buildUrl(list[hash(key) % list.length], width, height);
}

/**
 * Source for one gallery photo belonging to a vendor. Uploaded files get their
 * path resolved; billed-and-broken Google photos are dropped in favour of a
 * category image so the gallery never shows Google's error graphic.
 */
export function vendorPhotoSrc(photoUrl, vendor, width = 800, height = 500) {
  if (!photoUrl || isPaidGooglePhoto(photoUrl)) {
    return categoryImage(vendor?.category_slug, width, height);
  }
  return mediaUrl(photoUrl);
}

export default CATEGORY_IMAGES;
